/**
 * 「时间范围」URL 状态的**共享实现**（唯一来源，2026-10-05 第十五批）。
 *
 * 为什么单独抽一个模块：广告管理页与经营概览页现在**都用**同一套快捷选项
 * （`dateRange.ts` 的 `dateRangeOptions`），若 `from`/`to`/`range` 三个参数
 * 各写一套解析/序列化，迟早出现「同一屏两个区间」——用户第 2/3 条正是要求
 * 全站只有一个「最近 N 天」定义。
 *
 * 沿用 `urlState.ts` 的三条原则：
 *   1) URL 是时间范围状态的唯一真相（可分享、刷新还原、前进后退一致）；
 *   2) 默认值不写入 URL；
 *   3) 解析对垃圾输入免疫（用户会手改 URL），绝不抛错。
 *
 * ## `range` 的序列化规则（两页共用）
 *
 *  · `from` / `to` **总是写出**：链接自描述（用户要能一眼看到"最近 30 天到底是从哪天到哪天"）；
 *  · `range` **总是写出**（2026-10-05 第十七批改）：就绪度参考右端会让「最近 7 天」的
 *    区间与"昨天为右端"的推断差一天，省略 `range` 会在刷新后把档位降级成「自定义」
 *    （详见 `rangeSearchToParams` 的注释）。
 *  · 万一 `range` 缺失（手改 URL / 旧链接），解析侧仍用 `inferDateRangeKey`
 *    按 from/to **反推**高亮档位兜底。
 */

import {
  DEFAULT_DATE_RANGE_KEY,
  dateRangeKey,
  inferDateRangeKey,
  isValidIsoDate,
  parseDateRangeKey,
  resolveDateRange,
  type RangeReferenceEnd,
} from "./dateRange.js";

/** `from` / `to` / `range` 三件套（页面 search 至少包含这三个字段）。 */
export interface RangeSearch {
  /** 业务日 YYYY-MM-DD（含）。由 `range` 算出（`custom` 时来自 URL）。 */
  readonly from: string;
  readonly to: string;
  /** 时间范围快捷选项 key（见 `dateRange.ts`）；`custom` = 用户自己填的区间。 */
  readonly range: string;
  /**
   * 「含未结算」开关（2026-10-05 第二十三批 · 任务 4）。缺省 `false` = 保守口径
   * （窗口右端 = 就绪右端）；`true` = 右端切到今天，页面必须醒目警告
   * 「含未结算日，数字会随结算变化」。进 URL，可分享。
   */
  readonly includeUnsettled?: boolean;
}

/** 共享的 URL key（`stringifySearch` 用它识别 schema）。 */
export const RANGE_SEARCH_KEYS: readonly string[] = ["from", "to", "range", "includeUnsettled"];

function readString(raw: Record<string, unknown>, key: string): string | null {
  const value = raw[key];
  return typeof value === "string" ? value : null;
}

/**
 * URL 里的布尔（缺省 / 垃圾 → false，可分享链接友好）。
 *
 * ⚠️ 必须同时接受**真正的布尔 `true`**：TanStack Router 的 `stringifySearch` 会把
 * **已解析的 search 对象**（`includeUnsettled: true`）交回序列化器；只认字符串会让
 * 开关永远写不进 URL。
 */
function readBool(raw: Record<string, unknown>, key: string): boolean {
  const value = raw[key];
  return value === true || value === "1" || value === "true";
}

function isUsableRange(from: string, to: string): boolean {
  return isValidIsoDate(from) && isValidIsoDate(to) && from <= to;
}

/**
 * 默认区间 = `defaultRangeKey` 对应的 preset 算出来的闭区间。
 *
 * `defaultRangeKey` 允许是垃圾值（URL 可手改）：此时 `parseDateRangeKey` 会回落到
 * 「最近 7 天」，**绝不**产生非法区间。
 *
 * `referenceEnd`（2026-10-05 第十七批）= 「最近 N 天」的参考右端：
 *  · 缺省 / `null` → 既有「不含今天」`[今天-N, 今天-1]`（URL 解析路径的确定性兜底）；
 *  · 就绪度右端 → 「到数据齐到那天为止的 N 个整天」（页面在拿到 `/v1/data-readiness`
 *    后用它把默认区间对齐到已结算的日子，见 `lib/readinessRange.ts`）。
 */
export function defaultRangeSearch(
  defaultRangeKey: string,
  now: Date = new Date(),
  referenceEnd?: RangeReferenceEnd,
): RangeSearch {
  const preset = parseDateRangeKey(defaultRangeKey);
  const resolved = resolveDateRange(preset, now, referenceEnd);
  if (resolved === null) {
    // `custom` 型默认（没有具体区间）→ 用全站默认档兜底，保证 from/to 永远可用。
    const fallback = resolveDateRange(parseDateRangeKey(DEFAULT_DATE_RANGE_KEY), now, referenceEnd);
    if (fallback === null) throw new Error("默认时间范围不可解析：last7 必须存在");
    return { from: fallback.from, to: fallback.to, range: dateRangeKey(preset) };
  }
  return { from: resolved.from, to: resolved.to, range: dateRangeKey(preset) };
}

/**
 * URL → 区间。语义：
 *  · `range` 是可识别的非 custom 档 → 区间**由 preset 算**（URL 里过期的 from/to 不生效）；
 *  · 否则用 URL 的 from/to（合法且不倒置）→ 高亮用 `inferDateRangeKey` 反推；
 *  · from/to 缺失 / 非法 / 倒置 → 回落默认档（绝不把 400 输入带给后端）。
 *
 * `referenceEnd` 见 `defaultRangeSearch`：路由层校验时通常拿不到就绪度（首帧是同步的），
 * 因此这里**总是**用缺省（不含今天）算出一个确定性的 `from`/`to`；页面拿到就绪度后
 * 再用 `lib/readinessRange.ts` 的 `effectiveRange` 把「最近 N 天」对齐到就绪右端。
 */
export function parseRangeSearch(
  raw: Record<string, unknown>,
  defaultRangeKey: string,
  now: Date = new Date(),
  referenceEnd?: RangeReferenceEnd,
): RangeSearch {
  const fallback = defaultRangeSearch(defaultRangeKey, now, referenceEnd);
  const includeUnsettled = readBool(raw, "includeUnsettled");
  const rangeRaw = readString(raw, "range");
  const preset = parseDateRangeKey(rangeRaw);
  const recognized = rangeRaw !== null && dateRangeKey(preset) === rangeRaw;

  if (recognized && preset.kind !== "custom") {
    const resolved = resolveDateRange(preset, now, referenceEnd);
    if (resolved !== null) return { from: resolved.from, to: resolved.to, range: rangeRaw, ...(includeUnsettled ? { includeUnsettled: true } : {}) };
  }

  const candidateFrom = readString(raw, "from") ?? "";
  const candidateTo = readString(raw, "to") ?? "";
  if (isUsableRange(candidateFrom, candidateTo)) {
    // 显式 `range=custom` → 保持 custom（用户自己填的，不要自作聪明改标签）。
    const range = recognized ? "custom" : (inferDateRangeKey(candidateFrom, candidateTo, now, referenceEnd) ?? "custom");
    return { from: candidateFrom, to: candidateTo, range, ...(includeUnsettled ? { includeUnsettled: true } : {}) };
  }
  return { ...fallback, ...(includeUnsettled ? { includeUnsettled: true } : {}) };
}

/**
 * 序列化（共享规则，见文件头）：`from`/`to` **总是**写出，`range` 也**总是**写出。
 *
 * ⚠️ 2026-10-05 第十七批：此前 `range === last7`（全站默认档）时省略 `range`。
 * 引入就绪度参考右端后这**不再安全**：页面把「最近 7 天」解析成
 * `[readyThrough-6, readyThrough]`（凌晨的 readyThrough = D-2，与"昨天"差一天），
 * 省略 `range` 后重新解析时 `inferDateRangeKey`（参考右端=昨天）对不上，
 * 会把档位降级成「自定义」——用户没动过区间，按钮却跳了。
 * 因此改成**总是写出** `range`：链接自描述、档位永远能还原。
 *
 * 广告管理页与概览页**必须**用同一份实现，否则两个页面在同一 URL 上会各写一套。
 */
export function rangeSearchToParams(search: RangeSearch): Record<string, string> {
  return {
    from: search.from,
    to: search.to,
    range: search.range,
    // 默认值不写进 URL（原则 2）；打开时才写，链接可分享。
    ...(search.includeUnsettled === true ? { includeUnsettled: "1" } : {}),
  };
}

/**
 * 点了某个快捷档 → 要写进 URL 的补丁（两页共用，不许各写一套）。
 *
 *  · `custom`：只改档位，**保留当前 from/to**，让用户接着改（否则输入框会被清空）；
 *  · 其它档：区间**由 preset 现算**（`[referenceEnd-(N-1), referenceEnd]`；
 *    `referenceEnd` 缺省 = 昨天 → 既有「不含今天」），URL 里旧的 from/to 被覆盖；
 *  · 档位不可识别（URL 手改 / 未来档位）：只写 key，区间交给解析侧兜底。
 *
 * ⚠️ 页面**必须**把当前的就绪度右端作为 `referenceEnd` 传进来：这样用户点「最近 7 天」
 * 拿到的也是「7 个已结算的整天」，与默认区间同一口径（N 个整天对 N 个整天）。
 */
export function rangeSearchPickPatch(
  key: string,
  now: Date = new Date(),
  referenceEnd?: RangeReferenceEnd,
): Partial<RangeSearch> {
  const preset = parseDateRangeKey(key);
  if (preset.kind === "custom") return { range: "custom" };
  const resolved = resolveDateRange(preset, now, referenceEnd);
  if (resolved === null) return { range: key };
  return { range: dateRangeKey(preset), from: resolved.from, to: resolved.to };
}
